import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Clock, Phone } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

const md = {
  h2: (props) => <h2 className="font-display text-2xl md:text-3xl uppercase text-[#23282B] mt-10 mb-3" {...props} />,
  h3: (props) => <h3 className="font-display text-xl uppercase text-[#23282B] mt-8 mb-2" {...props} />,
  p: (props) => <p className="text-[15px] md:text-base text-[#2C3539]/85 leading-relaxed mb-4" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-1.5 text-[15px] md:text-base text-[#2C3539]/85" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-[15px] md:text-base text-[#2C3539]/85" {...props} />,
  strong: (props) => <strong className="font-bold text-[#23282B]" {...props} />,
  a: (props) => <a className="text-[#2F5F80] underline" {...props} />,
  blockquote: (props) => <blockquote className="border-l-4 border-[#2F5F80] bg-[#F4F5F6] px-4 py-3 my-4 text-sm italic" {...props} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageTitle(post ? post.title : "Blog", post?.excerpt);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/post/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center bg-[#F4F5F6]" data-testid="blog-post-notfound">
        <div className="font-display text-3xl uppercase text-[#23282B]">Post not found</div>
        <Link to="/blog" className="mt-4 text-[#2F5F80] text-sm font-bold uppercase tracking-wider inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back to the Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="blog-post-page">
      <section className="bg-[#23282B] av-grid text-white">
        <div className="max-w-[860px] mx-auto px-5 md:px-10 py-14 md:py-20">
          <Link to="/blog" data-testid="blog-post-back" className="text-[#9CC4DB] hover:text-white text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center gap-2">
            <ArrowLeft size={14} /> All Posts
          </Link>
          {post ? (
            <>
              <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-tight" data-testid="blog-post-title">{post.title}</h1>
              <div className={`mt-4 text-xs tracking-[0.2em] uppercase text-white/60 inline-flex items-center gap-2 ${post.header_image ? "mb-16 md:mb-20" : ""}`}>
                {post.published_at?.slice(0, 10)} · <Clock size={12} /> {post.reading_time} min read
              </div>
            </>
          ) : (
            <div className="mt-6 text-white/60">Loading…</div>
          )}
        </div>
      </section>

      {post && (
        <article className="max-w-[860px] mx-auto px-5 md:px-10 py-10 md:py-14" data-testid="blog-post-content">
          {post.header_image && (
            <div className="mb-8 -mt-20 md:-mt-24 relative border-4 border-white shadow-xl overflow-hidden">
              <img
                src={assetUrl(post.header_image)}
                alt={post.title}
                data-testid="blog-post-header-img"
                className="w-full aspect-[16/7] object-cover"
              />
              <div className="absolute bottom-0 left-0 bg-[#23282B]/85 text-white text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1.5">
                Real AV Contracting job site
              </div>
            </div>
          )}
          {post.excerpt && <p className="text-lg text-[#2C3539]/80 leading-relaxed border-l-4 border-[#2F5F80] pl-4 mb-8">{post.excerpt}</p>}
          <ReactMarkdown components={md}>{post.content_md}</ReactMarkdown>

          <div className="mt-12 bg-[#23282B] text-white p-7 md:p-9 border-l-4 border-[#2F5F80]">
            <div className="font-display text-2xl uppercase">Not sure what you're dealing with?</div>
            <p className="mt-2 text-white/75 text-sm leading-relaxed">Assessments are free. Send us a photo or give us a call — we'll tell you straight whether it's a DIY fix or a job for the crew.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="tel:+12508861112" data-testid="blog-post-call" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors">
                <Phone size={14} /> (250) 886-1112
              </a>
              <Link to="/contact" data-testid="blog-post-contact" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-[#9CC4DB] hover:text-[#9CC4DB] text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors">
                Request a Quote
              </Link>
            </div>
          </div>
        </article>
      )}
    </div>
  );
}
